<template>
  <div id="room-type-ref-prices">
    <v-card flat>
      <v-app-bar flat color="white">
        <v-toolbar-title class="title">
          {{ $t("Planning") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="secondary" rounded @click="openEditDialog()">
          <v-icon small class="mr-2">mdi-pencil</v-icon>
          {{ $t("Massive_edit") }}
        </v-btn>
      </v-app-bar>
      <v-card-text class="pt-0">
        <!-- Recàlcul de tarifes calculades en curs (no bloqueja la pantalla) -->
        <a-ref-recalc-progress @finished="loadData()" />

        <!-- Date range selector -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-text-field
              :value="getFormattedPeriod(filter_dates)"
              readonly
              outlined
              rounded
              dense
              hide-details
              append-icon="mdi-calendar"
              :label="$t('date_range')"
              @click="range_dialog = true"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="8">
            <!-- Help text -->
            <v-alert dense text type="info" class="mb-0 text-caption">
              {{ $t("calculated_rates_help_text") }}
            </v-alert>
          </v-col>
        </v-row>

        <!-- New Data table -->
        <a-ref-prices-table
          :rows="table_items"
          :columns="room_rate_columns"
          :selection="selection"
          :events_data="hotel_events_data"
          @cell-click="handleCellClick"
          @cell-hover="handleCellHoverCol"
          @cell-hover-date="handleCellHoverDate"
        />

        <div v-if="loading" class="d-flex justify-center mt-4">
          <v-progress-circular
            indeterminate
            color="secondary"
          ></v-progress-circular>
        </div>
      </v-card-text>
    </v-card>

    <!-- Selection hint -->
    <v-snackbar v-model="show_selection_hint" :timeout="-1" color="info" top>
      {{ $t("shift_click_end_date") }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="clearSelection()">
          {{ $t("cancel") }}
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Range Dialog -->
    <v-dialog
      v-model="range_dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="600"
    >
      <v-card>
        <v-card-title class="headline justify-space-between">
          {{ $t("select_date_range") }}
          <v-btn icon @click="range_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6" class="pa-1">
              <v-date-picker
                v-model="filter_dates"
                first-day-of-week="1"
                range
                :picker-date.sync="picker_date"
                no-title
                full-width
              ></v-date-picker>
            </v-col>
            <v-col cols="6" class="pa-1">
              <v-date-picker
                v-model="filter_dates"
                first-day-of-week="1"
                range
                :picker-date.sync="picker_date2"
                no-title
                full-width
              ></v-date-picker>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="secondary" rounded @click="applyRange()">
            {{ $t("apply") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog
      v-model="edit_dialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="600"
      @click:outside="clearSelection()"
    >
      <v-card>
        <v-card-title class="headline justify-space-between">
          {{ $t("edit_calculated_rates") }}
          <v-btn icon @click="edit_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <!-- Date range -->
          <div class="mb-4">
            <div class="text-caption grey--text mb-2">
              {{ $t("select_date_range") }}
            </div>
            <v-menu :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on: onDate }">
                <v-text-field
                  v-on="{ ...onDate }"
                  :value="getFormattedPeriod(edit_dates)"
                  readonly
                  outlined
                  rounded
                  dense
                  hide-details
                  append-icon="mdi-calendar"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="edit_dates"
                first-day-of-week="1"
                scrollable
                range
              ></v-date-picker>
            </v-menu>
          </div>

          <!-- Weekdays -->
          <div class="mb-4">
            <div class="text-caption grey--text mb-3">
              {{ $t("weekdays_included") }}
            </div>
            <div class="d-flex align-center" style="gap: 10px">
              <div
                v-for="day in weekdays"
                :key="day.value"
                class="d-flex flex-column align-center flex-grow-1"
              >
                <span class="mr-2">{{ day.name }}</span>
                <v-switch
                  v-model="selected_weekdays[day.value]"
                  color="secondary"
                  dense
                  hide-details
                  class="mt-0 pt-0"
                ></v-switch>
              </div>
            </div>
          </div>

          <!-- Edit mode selector -->
          <div class="mb-4">
            <div class="text-caption grey--text mb-3">
              {{ $t("edit_mode") }}
            </div>
            <v-btn-toggle v-model="edit_mode" mandatory dense rounded>
              <v-btn value="rules" small>
                <v-icon small class="mr-1">mdi-link-variant</v-icon>
                {{ $t("Rules") }}
              </v-btn>
              <v-btn value="available_rooms" small>
                <v-icon small class="mr-1">mdi-bed-outline</v-icon>
                {{ $t("available_rooms_label") }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <!-- Rules repeater -->
          <div v-if="edit_mode === 'rules'" class="mb-4">
            <div class="text-caption grey--text mb-3">
              {{ $t("Rules") }}
            </div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>{{ $t("category") }}</th>
                    <th>{{ $t("Reference") }}</th>
                    <th>{{ $t("type") }}</th>
                    <th>{{ $t("value") }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(rule, index) in rules" :key="index">
                    <td class="pl-0 pr-0">
                      <v-select
                        v-model="rule.room_rate_id"
                        :items="room_types_items"
                        item-text="name"
                        item-value="id"
                        outlined
                        rounded
                        dense
                        hide-details
                        class="my-2 mini-input"
                      ></v-select>
                    </td>
                    <td class="pr-0">
                      <v-select
                        v-model="rule.ref_room_rate_id"
                        :items="getAvailableRefRoomTypes(rule.room_rate_id)"
                        item-text="name"
                        item-value="id"
                        outlined
                        rounded
                        dense
                        hide-details
                        class="my-2 mini-input"
                      ></v-select>
                    </td>
                    <td class="pr-0">
                      <v-btn-toggle
                        v-if="rule.ref_room_rate_id"
                        v-model="rule.ref_operation"
                        mandatory
                        dense
                        rounded
                        class="my-2 mini-input"
                      >
                        <v-btn value="percent" small>
                          <v-icon small>mdi-percent-outline</v-icon>
                        </v-btn>
                        <v-btn value="absolute" small>
                          <v-icon small>mdi-currency-eur</v-icon>
                        </v-btn>
                      </v-btn-toggle>
                    </td>
                    <td class="pr-0" style="width: 110px;">
                      <v-text-field
                        v-if="rule.ref_room_rate_id"
                        v-model.number="rule.ref_value"
                        type="number"
                        :append-icon="
                          rule.ref_operation === 'percent'
                            ? 'mdi-percent-outline'
                            : 'mdi-currency-eur'
                        "
                        outlined
                        rounded
                        dense
                        hide-details
                        class="my-2 mini-input"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="removeRule(index)"
                      >
                        <v-icon small>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <div class="text-center">
              <v-btn
                outlined
                rounded
                color="secondary"
                @click="addRule()"
                class="mt-3"
              >
                <v-icon small class="mr-2">mdi-plus</v-icon>
                {{ $t("add_rule") }}
              </v-btn>
            </div>
          </div>

          <!-- Available rooms editor -->
          <div v-else class="mb-4">
            <div class="text-caption grey--text mb-3">
              {{ $t("available_rooms_label") }}
            </div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>{{ $t("category") }}</th>
                    <th>{{ $t("available_rooms_label") }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(ar, index) in available_rooms"
                    :key="ar.room_rate_id"
                  >
                    <td class="pl-0 pr-0">
                      {{ getRoomTypeName(ar.room_rate_id) }}
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="ar.available_rooms"
                        type="number"
                        min="0"
                        outlined
                        rounded
                        dense
                        hide-details
                        class="my-2 mini-input"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="removeAvailableRoom(index)"
                      >
                        <v-icon small>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <!-- Recàlcul: només té sentit editant regles amb referència.
               Les places disponibles no afecten els preus. -->
          <template v-if="can_recalculate">
            <v-divider class="mb-3"></v-divider>
            <v-checkbox
              v-model="recalculate"
              color="secondary"
              dense
              hide-details
              class="mt-0"
            >
              <template v-slot:label>
                <div>
                  <div>{{ $t("recalculate_modified_dates") }}</div>
                  <div class="text-caption grey--text">
                    {{
                      $t("recalculate_modified_dates_hint", {
                        count: estimated_changes
                      })
                    }}
                  </div>
                </div>
              </template>
            </v-checkbox>
            <v-alert
              v-if="recalculate && estimated_changes > recalc_warning_threshold"
              dense
              text
              type="warning"
              class="mt-3 mb-0 text-caption"
            >
              {{
                $t("recalculate_large_volume_warning", {
                  count: estimated_changes
                })
              }}
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions class="pt-0 pb-5 justify-center">
          <v-btn
            :disabled="!isValidForm()"
            color="secondary"
            rounded
            class="pl-5 pr-5"
            @click="saveRules()"
          >
            {{ $t("save") }}
          </v-btn>
          <v-btn
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="edit_dialog = false"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import aRefPricesTable from "@/components/settings/aRefPricesTable.vue";
import aRefRecalcProgress from "@/components/settings/aRefRecalcProgress.vue";
import * as Utils from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";

// A partir d'aquest nombre de tarifes estimades avisem del volum abans de desar:
// cada tarifa recalculada és un enviament al PMS.
const RECALC_WARNING_THRESHOLD = 500;

export default {
  name: "a-room-type-ref-prices",
  components: {
    aRefPricesTable,
    aRefRecalcProgress
  },
  data: () => ({
    filter_dates: [],
    range_dialog: false,
    picker_date: null,
    picker_date2: null,
    edit_dialog: false,
    edit_dates: [],
    selected_weekdays: {
      1: true,
      2: true,
      3: true,
      4: true,
      5: true,
      6: true,
      0: true
    },
    rules: [],
    loading: false,
    // Range selection state (for Shift+Click)
    selection_start_date: null,
    selection_end_date: null,
    selection_hover_date: null,
    selection_room_rate_ids: [],
    selection_start_col_index: -1,
    selection_hover_col_index: -1,
    // Edit mode state
    edit_mode: "rules", // "rules" o "available_rooms"
    available_rooms: [], // Array de { room_rate_id, available_rooms }
    // Recalcular els preus de les dates modificades (opt-in explícit)
    recalculate: false
  }),
  watch: {
    picker_date(val) {
      if (val) {
        this.picker_date2 = Vue.moment(val)
          .add(1, "month")
          .format("YYYY-MM");
      }
    },
    picker_date2(val) {
      if (val) {
        this.picker_date = Vue.moment(val)
          .subtract(1, "month")
          .format("YYYY-MM");
      }
    }
  },
  computed: {
    today() {
      return Vue.moment().format("YYYY-MM-DD");
    },
    show_selection_hint() {
      return (
        this.selection_start_date !== null &&
        this.selection_room_rate_ids.length > 0 &&
        !this.edit_dialog
      );
    },
    weekdays() {
      return [
        { name: this.$t("Monday"), value: 1 },
        { name: this.$t("Tuesday"), value: 2 },
        { name: this.$t("Wednesday"), value: 3 },
        { name: this.$t("Thursday"), value: 4 },
        { name: this.$t("Friday"), value: 5 },
        { name: this.$t("Saturday"), value: 6 },
        { name: this.$t("Sunday"), value: 0 }
      ];
    },
    room_types_items() {
      return this.room_types_mapped.map(rt => ({
        id: rt.id,
        name: rt.name
      }));
    },
    recalc_warning_threshold() {
      return RECALC_WARNING_THRESHOLD;
    },
    // Recalcular només té sentit si estem editant regles i alguna té referència:
    // sense referència no hi ha res a derivar, i les places no toquen els preus.
    can_recalculate() {
      return (
        this.edit_mode === "rules" &&
        this.rules.some(rule => !!rule.ref_room_rate_id)
      );
    },
    // Dates del rang que compleixen els dies de la setmana seleccionats.
    selected_dates_count() {
      if (!this.edit_dates || this.edit_dates.length < 2) {
        return 0;
      }
      const sortedDates = [...this.edit_dates].sort();
      const current = Vue.moment(sortedDates[0]);
      const end = Vue.moment(sortedDates[1]);
      let count = 0;
      while (current.isSameOrBefore(end, "day")) {
        if (this.selected_weekdays[current.day()]) {
          count++;
        }
        current.add(1, "day");
      }
      return count;
    },
    estimated_changes() {
      return (
        this.selected_dates_count *
        this.rules.filter(rule => !!rule.ref_room_rate_id).length
      );
    },
    room_rate_columns() {
      if (
        !this.room_rate_ref_period ||
        this.room_rate_ref_period.length === 0
      ) {
        return [];
      }
      // Get unique room rates from first date
      const firstDate = this.room_rate_ref_period[0];
      if (!firstDate || !firstDate.room_rates) {
        return [];
      }
      return firstDate.room_rates.map(rr => {
        const roomType = this.getRoomTypeById(rr.room_rate_id);
        return {
          room_rate_id: rr.room_rate_id,
          room_rate_name: roomType.name,
          room_rate_code: roomType.code
        };
      });
    },
    circular_errors() {
      const errors = {};
      // Build a map of dependencies: room_rate_id -> ref_room_rate_id
      const dependencyMap = {};
      this.rules.forEach(rule => {
        if (rule.room_rate_id && rule.ref_room_rate_id) {
          dependencyMap[rule.room_rate_id] = rule.ref_room_rate_id;
        }
      });

      this.rules.forEach((rule, index) => {
        if (!rule.room_rate_id || !rule.ref_room_rate_id) return;

        let currentRef = rule.ref_room_rate_id;
        const visited = new Set([rule.room_rate_id]);

        // Trace dependencies
        while (currentRef) {
          // If we encounter the starting room_rate_id, it's a cycle
          if (currentRef === rule.room_rate_id) {
            const roomTypeName = this.getRoomTypeById(rule.room_rate_id)
              ? this.getRoomTypeById(rule.room_rate_id).name
              : "";
            errors[index] = this.$t("circular_reference_error", {
              name: roomTypeName
            });
            break;
          }

          if (visited.has(currentRef)) {
            break;
          }

          visited.add(currentRef);
          currentRef = dependencyMap[currentRef];
        }
      });
      return errors;
    },
    table_items() {
      if (!this.room_rate_ref_period) {
        return [];
      }
      return this.room_rate_ref_period.map(dateItem => {
        const row = {
          date: dateItem.date,
          day_of_week: dateItem.day_of_week,
          room_rates: dateItem.room_rates
        };
        return row;
      });
    },
    selection() {
      let room_rate_ids = this.selection_room_rate_ids;

      if (
        this.selection_start_date &&
        !this.selection_end_date &&
        this.selection_start_col_index !== -1 &&
        this.selection_hover_col_index !== -1
      ) {
        const minCol = Math.min(
          this.selection_start_col_index,
          this.selection_hover_col_index
        );
        const maxCol = Math.max(
          this.selection_start_col_index,
          this.selection_hover_col_index
        );

        room_rate_ids = [];
        for (let i = minCol; i <= maxCol; i++) {
          if (this.room_rate_columns[i]) {
            room_rate_ids.push(this.room_rate_columns[i].room_rate_id);
          }
        }
      }

      return {
        start_date: this.selection_start_date,
        end_date: this.selection_end_date,
        hover_date: this.selection_hover_date,
        room_rate_ids: room_rate_ids
      };
    },
    hotel_events_data() {
      const data = {};
      if (
        this.calendar_store.hotel_comparative_dates &&
        Object.keys(this.calendar_store.hotel_comparative_dates).length > 0
      ) {
        for (const date in this.calendar_store.hotel_comparative_dates) {
          data[date] = this.calendar_store.hotel_comparative_dates[date].events;
        }
      }
      return data;
    },
    ...mapState({
      room_types_mapped: state => state.roomtype.types_mapped,
      room_rate_ref_period: state => state.roomtype.room_rate_ref_period,
      calendar_store: state => state.calendar
    }),
    ...mapGetters({
      current_hotel: "current_hotel",
      getRoomTypeById: "getRoomTypeById"
    })
  },
  methods: {
    applyRange() {
      this.range_dialog = false;
      this.loadData();
    },
    getAvailableRefRoomTypes(currentRoomRateId) {
      // Add a "None" option to clear the reference
      const noneOption = { id: null, name: this.$t("none") };

      if (!currentRoomRateId) return [noneOption, ...this.room_types_items];

      const filtered = this.room_types_items.filter(
        rt => rt.id !== currentRoomRateId
      );
      return [noneOption, ...filtered];
    },
    getFormattedPeriod(dates) {
      if (!dates || dates.length < 2) {
        return "";
      }
      const sortedDates = [...dates].sort();
      const fromDate = Vue.moment(sortedDates[0]).format("DD MMMM YYYY");
      const toDate = Vue.moment(sortedDates[1]).format("DD MMMM YYYY");
      // If same date, show only once
      if (sortedDates[0] === sortedDates[1]) {
        return fromDate;
      }
      return `${fromDate} - ${toDate}`;
    },
    getHotelComparativeDates() {
      if (this.filter_dates && this.filter_dates.length >= 2) {
        const sortedDates = [...this.filter_dates].sort();
        this.$store
          .dispatch("getHotelComparativeDates", {
            hotel_id: this.current_hotel.id,
            year: Vue.moment(sortedDates[0]).format("YYYY"),
            from_date: sortedDates[0],
            to_date: sortedDates[1]
          })
          .catch(() => {
            //console.error("Error fetching hotel comparative dates");
          });
      }
    },
    loadData() {
      if (!this.filter_dates || this.filter_dates.length < 2) {
        return;
      }
      const sortedDates = [...this.filter_dates].sort();
      this.loading = true;
      this.$store
        .dispatch("fetchRoomRateRefPeriod", {
          from_date: sortedDates[0],
          to_date: sortedDates[1]
        })
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          Utils.showError(this, "something_went_wrong");
        });

      this.$store
        .dispatch("getHotelComments", {
          hotel_id: this.current_hotel.id
        })
        .catch(() => {
          //console.error("Error fetching hotel comments");
        });

      this.getHotelComparativeDates();
    },
    openEditDialog(preselectedRoomRate = null, customDates = null) {
      // Initialize with custom dates, filter dates or default
      if (customDates && customDates.length === 2) {
        this.edit_dates = [...customDates].sort();
      } else if (this.filter_dates && this.filter_dates.length === 2) {
        this.edit_dates = [...this.filter_dates];
      } else {
        const fromDate = Vue.moment().format("YYYY-MM-DD");
        const toDate = Vue.moment()
          .add(30, "days")
          .format("YYYY-MM-DD");
        this.edit_dates = [fromDate, toDate];
      }
      // Reset weekdays
      this.selected_weekdays = {
        1: true,
        2: true,
        3: true,
        4: true,
        5: true,
        6: true,
        0: true
      };
      // Reset edit mode
      this.edit_mode = "rules";
      this.recalculate = false;
      // Reset rules and add preselected if provided
      this.rules = [];
      if (preselectedRoomRate) {
        this.rules.push({
          room_rate_id: preselectedRoomRate.room_rate_id,
          ref_room_rate_id: preselectedRoomRate.ref_room_rate_id || null,
          ref_operation: preselectedRoomRate.ref_operation || "percent",
          ref_value: preselectedRoomRate.ref_value || 0
        });
      }
      // Initialize available_rooms from room_types_items
      this.initAvailableRooms(
        preselectedRoomRate ? [preselectedRoomRate.room_rate_id] : null,
        customDates
      );
      this.edit_dialog = true;
    },
    openEditDialogWithRules(rulesArray, customDates = null) {
      // Initialize with custom dates or filter dates
      if (customDates && customDates.length === 2) {
        this.edit_dates = [...customDates].sort();
      } else if (this.filter_dates && this.filter_dates.length === 2) {
        this.edit_dates = [...this.filter_dates];
      } else {
        const fromDate = Vue.moment().format("YYYY-MM-DD");
        const toDate = Vue.moment()
          .add(30, "days")
          .format("YYYY-MM-DD");
        this.edit_dates = [fromDate, toDate];
      }
      // Reset weekdays
      this.selected_weekdays = {
        1: true,
        2: true,
        3: true,
        4: true,
        5: true,
        6: true,
        0: true
      };
      // Reset edit mode
      this.edit_mode = "rules";
      this.recalculate = false;
      // Set rules from the array
      this.rules = rulesArray.map(rule => ({
        room_rate_id: rule.room_rate_id,
        ref_room_rate_id: rule.ref_room_rate_id || null,
        ref_operation: rule.ref_operation || "percent",
        ref_value: rule.ref_value || 0
      }));
      // Initialize available_rooms from selected room_rate_ids
      const roomRateIds = rulesArray.map(r => r.room_rate_id);
      this.initAvailableRooms(roomRateIds, customDates);
      this.edit_dialog = true;
    },
    handleCellClick({ date, room_rate_id, shiftKey }) {
      // SHIFT+CLICK: Range selection mode
      if (shiftKey) {
        // If no selection started, start new selection
        if (this.selection_start_date === null) {
          this.selection_start_date = date;
          this.selection_room_rate_ids = [room_rate_id];
          this.selection_start_col_index = this.room_rate_columns.findIndex(
            c => c.room_rate_id === room_rate_id
          );
          this.selection_hover_col_index = this.selection_start_col_index;
          return;
        }

        // We have a selection - complete it
        this.selection_end_date = date;

        // Calculate final column range
        const endColIndex = this.room_rate_columns.findIndex(
          c => c.room_rate_id === room_rate_id
        );

        const minCol = Math.min(this.selection_start_col_index, endColIndex);
        const maxCol = Math.max(this.selection_start_col_index, endColIndex);

        this.selection_room_rate_ids = [];
        for (let i = minCol; i <= maxCol; i++) {
          if (this.room_rate_columns[i]) {
            this.selection_room_rate_ids.push(
              this.room_rate_columns[i].room_rate_id
            );
          }
        }

        // Sort dates for the range
        const dates = [
          this.selection_start_date,
          this.selection_end_date
        ].sort();

        // Build rules for all selected room_rate_ids
        const rules = this.selection_room_rate_ids.map(rr_id => {
          const startItem = this.table_items.find(
            i => i.date === this.selection_start_date
          );
          const roomRateData =
            startItem && startItem.room_rates
              ? startItem.room_rates.find(rr => rr.room_rate_id === rr_id)
              : null;

          return {
            room_rate_id: rr_id,
            ref_room_rate_id: roomRateData
              ? roomRateData.ref_room_rate_id
              : null,
            ref_operation: roomRateData
              ? roomRateData.ref_operation
              : "percent",
            ref_value: roomRateData ? roomRateData.ref_value : 0
          };
        });

        // Open dialog with selected range and all rules
        this.openEditDialogWithRules(rules, dates);

        // Reset selection state
        this.clearSelection();
        return;
      }

      // CLICK WITHOUT SHIFT - clear any selection and open single cell dialog
      this.clearSelection();

      // SIMPLE CLICK: Open dialog immediately with single date
      const startItem = this.table_items.find(i => i.date === date);
      const roomRateData =
        startItem && startItem.room_rates
          ? startItem.room_rates.find(rr => rr.room_rate_id === room_rate_id)
          : null;

      // Open dialog with single date (date repeated for range)
      this.openEditDialog(
        {
          room_rate_id: room_rate_id,
          ref_room_rate_id: roomRateData ? roomRateData.ref_room_rate_id : null,
          ref_operation: roomRateData ? roomRateData.ref_operation : "percent",
          ref_value: roomRateData ? roomRateData.ref_value : 0
        },
        [date, date]
      );
    },
    handleCellHoverCol(roomRateId) {
      if (this.selection_start_date !== null) {
        this.selection_hover_col_index = this.room_rate_columns.findIndex(
          c => c.room_rate_id === roomRateId
        );
      }
    },
    handleCellHoverDate(date) {
      // Update hover date for visual preview during shift selection
      if (this.selection_start_date !== null) {
        this.selection_hover_date = date;
      }
    },
    clearSelection() {
      this.selection_start_date = null;
      this.selection_end_date = null;
      this.selection_hover_date = null;
      this.selection_room_rate_ids = [];
      this.selection_start_col_index = -1;
      this.selection_hover_col_index = -1;
    },
    addRule() {
      this.rules.push({
        room_rate_id: null,
        ref_room_rate_id: null,
        ref_operation: "percent",
        ref_value: 0
      });
    },
    removeRule(index) {
      this.rules.splice(index, 1);
    },
    removeAvailableRoom(index) {
      this.available_rooms.splice(index, 1);
    },
    isValidForm() {
      if (!this.edit_dates || this.edit_dates.length < 2) {
        return false;
      }
      if (!Object.values(this.selected_weekdays).some(day => day === true)) {
        return false;
      }
      // Validate based on edit mode
      if (this.edit_mode === "rules") {
        if (this.rules.length === 0) {
          return false;
        }
        // Check all rules have required fields
        return this.rules.every(
          rule =>
            rule.room_rate_id !== null &&
            // Allow ref_room_rate_id to be null (to clear reference)
            rule.ref_operation !== null
        );
      } else {
        // available_rooms mode - just need at least one room to edit
        return this.available_rooms.length > 0;
      }
    },
    saveRules() {
      const sortedDates = [...this.edit_dates].sort();
      const weekdays = Object.keys(this.selected_weekdays)
        .filter(key => this.selected_weekdays[key])
        .map(key => parseInt(key));

      this.$store.dispatch("setSnackbar", {
        title: this.$t("saving_calculated_rates"),
        color: "secondary"
      });

      // Build payload - always include both refs and availabilities
      const payload = {
        hotel_id: this.current_hotel.id,
        from_date: sortedDates[0],
        to_date: sortedDates[1],
        weekdays: weekdays,
        refs: [],
        availabilities: [],
        recalculate: this.can_recalculate && this.recalculate
      };

      //console.log(this.edit_mode);

      if (this.edit_mode === "rules") {
        // Fill refs when editing rules
        payload.refs = this.rules.map(rule => {
          if (!rule.ref_room_rate_id) {
            return {
              room_rate_id: rule.room_rate_id,
              ref_room_rate_id: null,
              ref_operation: null,
              ref_value: null
            };
          }
          return rule;
        });
      } else {
        // Fill availabilities when editing availability
        payload.availabilities = this.available_rooms.map(ar => ({
          room_rate_id: ar.room_rate_id,
          available_rooms: ar.available_rooms
        }));
      }

      //console.log(payload);

      this.$store
        .dispatch("setRoomRateRefPeriod", payload)
        .then(response => {
          this.edit_dialog = false;
          const snackbar = {
            title: this.$t("saved_calculated_rates"),
            color: "accent"
          };
          if (response && response.recalc) {
            // El recàlcul va en segon pla; oferim l'enllaç al registre de canvis
            // només si l'usuari hi té permís (ho resol rateChangeLogSnackbarExtras).
            const extras = Utils.rateChangeLogSnackbarExtras(this);
            snackbar.text = this.$t("recalculation_started");
            if (extras.link) {
              snackbar.link = extras.link;
            }
          }
          this.$store.dispatch("setSnackbar", snackbar);
          // Reload data
          this.loadData();
        })
        .catch(() => {
          Utils.showError(this, "something_went_wrong");
        });
    },
    initAvailableRooms(selectedRoomRateIds = null, customDates = null) {
      // Get room rate ids to initialize
      const roomRateIds =
        selectedRoomRateIds || this.room_types_items.map(rt => rt.id);

      // Try to get current values from table_items for the first selected date
      const targetDate = customDates
        ? customDates[0]
        : this.edit_dates.length > 0
        ? this.edit_dates[0]
        : null;
      const dateItem = targetDate
        ? this.table_items.find(i => i.date === targetDate)
        : null;

      this.available_rooms = roomRateIds.map(roomRateId => {
        // Try to get existing value
        let currentValue = null;
        if (dateItem && dateItem.room_rates) {
          const roomRateData = dateItem.room_rates.find(
            rr => rr.room_rate_id === roomRateId
          );
          if (roomRateData) {
            currentValue = roomRateData.available_rooms;
          }
        }
        return {
          room_rate_id: roomRateId,
          available_rooms: currentValue
        };
      });
    },
    getRoomTypeName(roomRateId) {
      const roomType = this.getRoomTypeById(roomRateId);
      return roomType ? roomType.name : "";
    }
  },
  mounted() {
    // Initialize with default date range (next 30 days)
    const fromDate = Vue.moment().format("YYYY-MM-DD");
    const toDate = Vue.moment()
      .add(30, "days")
      .format("YYYY-MM-DD");
    this.filter_dates = [fromDate, toDate];
    this.loadData();
  }
};
</script>

<style lang="scss" scoped>
#room-type-ref-prices {
  ::v-deep .kpi-wrapper-2 {
    font-size: 18px !important;
    justify-content: flex-end !important;
  }
}
.mini-input {
  ::v-deep .v-input__slot {
    min-height: 35px !important;
    padding-left: 15px !important;
    padding-right: 10px !important;
    .v-select__selection {
      margin: 0 !important;
    }
    input {
      padding-top: 4px !important;
      padding-bottom: 4px !important;
    }
    .v-input__append-inner {
      margin-top: 5px !important;
    }
    .v-select__selection {
      font-size: 14px !important;
    }
    .mdi-currency-eur,
    .mdi-percent-outline {
      font-size: 16px !important;
    }
  }
  &.v-item-group {
    ::v-deep .v-btn {
      padding: 0 !important;
      min-width: 35px !important;
    }
  }
}
</style>
